import { useState, useEffect } from 'react';
import { Mail, CheckCircle, XCircle, Loader2, Zap, Plus, AlertCircle, Trash2, RefreshCw, PowerOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { canDisconnectConnection } from '../../utils/permissions';
import { emailConnectionService } from '../../services/emailConnectionService';
import type { EmailConnection, EmailProvider } from '../../types/email';

interface ProviderConfig {
  id: EmailProvider;
  name: string;
  description: string;
  iconBg: string;
}

const PROVIDERS: ProviderConfig[] = [
  {
    id: 'google',
    name: 'Gmail',
    description: 'Google Workspace',
    iconBg: 'from-red-50 to-orange-50',
  },
  {
    id: 'microsoft',
    name: 'Microsoft Outlook',
    description: 'Microsoft 365',
    iconBg: 'from-blue-50 to-sky-50',
  },
];

const STORAGE_KEYS: Record<EmailProvider, string> = {
  google: 'oauth-result-google-email',
  microsoft: 'oauth-result-microsoft-email',
};

function ProviderIcon({ provider, size = 16 }: { provider: EmailProvider; size?: number }) {
  if (provider === 'google') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <path d="M2 6l10 7 10-7v12H2V6z" fill="#0078D4" opacity="0.8"/>
      <path d="M22 6l-10 7L2 6h20z" fill="#0078D4"/>
    </svg>
  );
}

export function EmailsConnectionSection() {
  const { user } = useAuth();
  const [connections, setConnections] = useState<EmailConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showProviderPicker, setShowProviderPicker] = useState(false);

  const loadConnections = async () => {
    if (!user?.client_id) return;
    try {
      setLoading(true);
      const data = await emailConnectionService.getEmailConnections(user.client_id);
      setConnections(data);
    } catch (err) {
      console.error('Error loading email connections:', err);
      setError('Failed to load email connections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnections();
  }, [user]);

  const openOAuthPopup = (authUrl: string, storageKey: string, providerName: string) => {
    setConnecting(true);
    setError(null);
    localStorage.removeItem(storageKey);

    try {
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        `${providerName}-email-oauth-popup`,
        `width=${width},height=${height},left=${left},top=${top}`
      );

      let timeoutId: number | null = null;
      let pollId: number | null = null;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (pollId) clearInterval(pollId);
      };

      pollId = window.setInterval(() => {
        if (popup && popup.closed) {
          const raw = localStorage.getItem(storageKey);
          if (!raw) {
            cleanup();
            setConnecting(false);
            return;
          }
        }

        const raw = localStorage.getItem(storageKey);
        if (!raw) return;

        cleanup();
        localStorage.removeItem(storageKey);

        try {
          const data = JSON.parse(raw);
          if (data.success) {
            setSuccessMessage(`${providerName} connected successfully!`);
            setTimeout(() => setSuccessMessage(null), 5000);
            loadConnections();
          } else {
            setError(data.error || `Failed to connect ${providerName}`);
          }
        } catch {
          setError(`Failed to connect ${providerName}`);
        }
        setConnecting(false);
        setShowProviderPicker(false);
      }, 500);

      timeoutId = window.setTimeout(() => {
        cleanup();
        setConnecting(false);
        setError('Connection timed out. Please try again.');
      }, 120000);
    } catch (err) {
      console.error(`Error initiating ${providerName} OAuth:`, err);
      setError('Failed to start connection process.');
      setConnecting(false);
    }
  };

  const handleConnect = async (provider: EmailProvider) => {
    if (!user?.client_id) return;

    const activeCount = connections.filter(c => c.is_active).length;
    if (activeCount >= emailConnectionService.maxAccounts) {
      setError(`Maximum of ${emailConnectionService.maxAccounts} email accounts reached`);
      return;
    }

    const storageKey = STORAGE_KEYS[provider];

    try {
      if (provider === 'google') {
        const authUrl = await emailConnectionService.initiateGoogleOAuth(user.client_id, user.email || '');
        openOAuthPopup(authUrl, storageKey, 'Gmail');
      } else if (provider === 'microsoft') {
        const authUrl = await emailConnectionService.initiateMicrosoftOAuth(user.client_id, user.email || '');
        openOAuthPopup(authUrl, storageKey, 'Microsoft Outlook');
      }
    } catch (err) {
      console.error('Error initiating email OAuth:', err);
      setError('Failed to start connection process.');
    }
  };

  const handleReconnect = (connection: EmailConnection) => {
    handleConnect(connection.provider);
  };

  const handleDisconnect = async (connection: EmailConnection) => {
    const label = connection.email || 'this email account';
    if (!confirm(`Are you sure you want to disconnect ${label}?`)) return;

    try {
      setDisconnectingId(connection.id);
      const result = await emailConnectionService.disconnectEmail(
        connection.id,
        connection.client_id,
        connection.provider
      );
      if (result.success) {
        await loadConnections();
        setSuccessMessage('Email account disconnected');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(result.error || 'Failed to disconnect');
      }
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError('Failed to disconnect email');
    } finally {
      setDisconnectingId(null);
    }
  };

  const handleDelete = async (connection: EmailConnection) => {
    const label = connection.email || 'this email account';
    if (!confirm(`Are you sure you want to permanently delete ${label}? This cannot be undone.`)) return;

    try {
      setDeletingId(connection.id);
      const result = await emailConnectionService.deleteEmailConnection(
        connection.id,
        connection.client_id
      );
      if (result.success) {
        await loadConnections();
        setSuccessMessage('Email account deleted');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(result.error || 'Failed to delete');
      }
    } catch (err) {
      console.error('Error deleting:', err);
      setError('Failed to delete email connection');
    } finally {
      setDeletingId(null);
    }
  };

  const getProviderLabel = (provider: EmailProvider) => {
    return provider === 'google' ? 'Gmail' : 'Outlook';
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl p-12 text-center">
        <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin mx-auto" />
        <p className="text-gray-500 mt-4">Loading email accounts...</p>
      </div>
    );
  }

  const hasConnections = connections.length > 0;
  const activeCount = connections.filter(c => c.is_active).length;
  const canAddMore = activeCount < emailConnectionService.maxAccounts;

  if (!hasConnections) {
    return (
      <div className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
            <div className="w-2 h-2 bg-gray-400 rounded-full" />
            <span>No email connected</span>
          </div>
        </div>

        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-[#1875FF] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700">
              Connect your email account. SalesPad will send and receive emails directly from your inbox.
            </p>
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-sm font-bold text-gray-900 font-display mb-2">Why connect your email?</h4>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-[#1875FF] flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">Send emails directly from your own email address</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-[#1875FF] flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">Receive and track replies in real-time</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-[#1875FF] flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">Keep all email conversations in one place</span>
            </li>
          </ul>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PROVIDERS.map((provider) => (
            <div
              key={provider.id}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md hover:border-gray-300 transition-all duration-200 flex flex-col"
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${provider.iconBg} flex items-center justify-center mb-4`}>
                <ProviderIcon provider={provider.id} size={28} />
              </div>
              <h3 className="text-base font-bold text-gray-900 font-display mb-1">{provider.name}</h3>
              <p className="text-sm text-gray-500 mb-4 flex-1">{provider.description}</p>

              <button
                onClick={() => handleConnect(provider.id)}
                disabled={connecting}
                className="w-full px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white font-medium rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {connecting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Connecting...</span>
                  </>
                ) : (
                  <span>Connect</span>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex items-center gap-2.5">
          <Zap className="w-4 h-4 text-[#1875FF] flex-shrink-0" />
          <div>
            <span className="text-xs font-bold text-gray-900 font-display">Email Sync Active</span>
            <span className="text-xs text-gray-500 ml-1.5">
              &mdash; Agents send, receive, and read emails from your connected accounts.
            </span>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-b-2 border-[#1875FF]/10">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Name
                </th>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Email
                </th>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Country
                </th>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Provider
                </th>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Score
                </th>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Status
                </th>
                <th className="w-10 py-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white/50">
              {connections.map((connection) => (
                <tr key={connection.id} className="hover:bg-[#1875FF]/5 transition-all duration-200">
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900">
                      {connection.display_name || ''}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-700">{connection.email || ''}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-700">{connection.country || ''}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <ProviderIcon provider={connection.provider} size={16} />
                      <span className="text-sm text-gray-700">{getProviderLabel(connection.provider)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {connection.reputation_score != null ? (
                      <span
                        className={`text-sm font-semibold ${
                          connection.reputation_score >= 80
                            ? 'text-[#05E0D1]'
                            : connection.reputation_score >= 50
                              ? 'text-amber-500'
                              : 'text-[#FF003C]'
                        }`}
                      >
                        {connection.reputation_score}%
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">&mdash;</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {connection.is_active ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        Active
                      </span>
                    ) : (
                      <button
                        onClick={() => handleReconnect(connection)}
                        disabled={connecting}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white hover:shadow-md transition-all disabled:opacity-50"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Reconnect
                      </button>
                    )}
                  </td>
                  <td className="py-4 pr-4 text-center">
                    {canDisconnectConnection(user, connection.user_id) && (
                      connection.is_active ? (
                        <button
                          onClick={() => handleDisconnect(connection)}
                          disabled={disconnectingId === connection.id}
                          className="inline-flex items-center justify-center p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-[#1875FF]/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Disconnect"
                        >
                          {disconnectingId === connection.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <PowerOff className="w-4 h-4" />
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDelete(connection)}
                          disabled={deletingId === connection.id}
                          className="inline-flex items-center justify-center p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-[#1875FF]/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete"
                        >
                          {deletingId === connection.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {canAddMore ? (
        <>
          {!showProviderPicker ? (
            <button
              onClick={() => setShowProviderPicker(true)}
              disabled={connecting}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white font-medium rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {connecting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Connecting...</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span className="text-sm">Connect Another Account</span>
                </>
              )}
            </button>
          ) : (
            <div className="space-y-3 p-4 bg-white rounded-xl border border-gray-200">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-gray-900 font-display">Choose email provider</h4>
                <button
                  onClick={() => setShowProviderPicker(false)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {PROVIDERS.map((provider) => (
                  <button
                    key={provider.id}
                    onClick={() => handleConnect(provider.id)}
                    disabled={connecting}
                    className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:shadow-md hover:border-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex-shrink-0">
                      <ProviderIcon provider={provider.id} size={22} />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900">{provider.name}</p>
                      <p className="text-xs text-gray-500">{provider.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700">
            Maximum of {emailConnectionService.maxAccounts} email accounts reached. Disconnect an account to add a new one.
          </p>
        </div>
      )}
    </div>
  );
}
